import { create } from "zustand";
import { makeHistorySnapshot } from "./historyStore";
import { getSceneActions, getSceneState } from "./sceneStore";
import {
  createActuatorControlPayload,
  createPositionActuatorControlPayload,
  inferActuatorControlNamingModeFromXml,
  setActiveActuatorControlNamingMode,
  updateGlobalActuatorMapping,
} from "../physics/actuatorMapping";
import { buildBodyRootMap, findBodyForJointUuid } from "../utils/bodyHierarchy";
import {
  generateActuatorControlName,
  generateStableActuatorNameFromUuid,
} from "../utils/actuatorIds";
import { mapAffinePositionTargetToControl } from "../physics/actuatorStartupControls";
import { getThreadedWasmSupport } from "../physics/threadedWasmSupport";
import { getPhysicsWorker, setPhysicsWorker } from "../physics/workerHandle";
import { getLatestMujocoWorkerResourceUrl } from "../physics/workerResourceDiagnostics";
import type { PhysicsStartOptions, StartupPosePolicy } from "../physics/startupPosePolicy";
import { addBreadcrumb } from "../monitoring/sentry";
import { getSceneObservabilitySummary } from "../monitoring/sceneObservability";
import { reportRuntimeError } from "../monitoring/runtimeErrors";
import { logger } from "../utils/logger";
import type { Equality, Joint, Scene } from "../types/scene";
import {
  buildCurrentSceneInitialJointMap,
  getInitialFreeJointPoseFromScene,
  getJointValuesFromMap,
  getSceneFixedTendonLengthForActuator,
  type PositionLikeActuator,
  type TendonRecord,
} from "./physics/physicsStartupHelpers";
import { createPhysicsWorkerMessageHandler } from "./physics/physicsWorkerMessages";
import { physicsUserErrorToast } from "./physics/physicsUserErrors";
import { exportMainXml, exportWorkerXml } from "./physics/physicsXmlExport";

const devLog = logger.debug;

type JointRecord = Record<string, Joint>;
type SnapshotWithScene = { scene?: Scene };

let pendingHelloTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingHelloWorker: Worker | null = null;
let livenessTimeout: ReturnType<typeof setTimeout> | null = null;
let livenessWorker: Worker | null = null;
let livenessStepCount: number | null = null;

const PHYSICS_WORKER_STALL_TIMEOUT_MS = 8000;

const physicsErrorToastDetail = {
  tone: "warning",
  action: {
    label: "here.",
    event: "physics-error-send-feedback",
  },
  durationMs: 12000,
};

const physicsCapabilityUnavailableToastDetail = {
  tone: "warning",
  durationMs: 12000,
};

const showPhysicsCapabilityUnavailableToast = (message: string) => {
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: {
          message: physicsUserErrorToast(message),
          ...physicsCapabilityUnavailableToastDetail,
        },
      })
    );
  } catch {
    // The store state still records the unavailable capability when toast UI is absent.
  }
};

const showPhysicsErrorToast = (message: string) => {
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: {
          message: physicsUserErrorToast(message),
          ...physicsErrorToastDetail,
        },
      })
    );
  } catch {
    // The store state still records the failure when toast UI is absent.
  }
};

const isLocalPhysicsFailureTestEnabled = () => {
  try {
    const hostname = globalThis.location?.hostname || "";
    const isLocalHost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname.endsWith(".localhost");
    if (!isLocalHost) return false;
    const params = new URLSearchParams(globalThis.location?.search || "");
    return params.has("forcePhysicsFailure");
  } catch {
    return false;
  }
};

export const getMujocoWasmRuntimeInfo = () => ({
  label: "MuJoCo 3.6.1",
  detail: "public/mujoco3",
});

const clearPendingHelloTimeout = (worker?: Worker | null) => {
  if (worker && pendingHelloWorker && worker !== pendingHelloWorker) return;
  if (pendingHelloTimeout) {
    clearTimeout(pendingHelloTimeout);
    pendingHelloTimeout = null;
  }
  if (!worker || worker === pendingHelloWorker) {
    pendingHelloWorker = null;
  }
};

type WorkerLivenessPayload = {
  simStepCount?: unknown;
  running?: unknown;
  paused?: unknown;
};

const clearPhysicsWorkerWatchdog = (worker?: Worker | null) => {
  if (worker && livenessWorker && worker !== livenessWorker) return;
  if (livenessTimeout) {
    clearTimeout(livenessTimeout);
    livenessTimeout = null;
  }
  if (!worker || worker === livenessWorker) {
    livenessWorker = null;
    livenessStepCount = null;
  }
};

const armPhysicsWorkerWatchdog = (
  worker: Worker,
  get: () => PhysicsState,
  set: (partial: Partial<PhysicsState>) => void
) => {
  if (livenessTimeout) clearTimeout(livenessTimeout);
  livenessWorker = worker;
  livenessTimeout = setTimeout(() => {
    if (livenessWorker !== worker) return;
    const state = get();
    if (state.status !== "running" || state.isPaused) return;

    const message = "Physics worker stalled - no simulation progress received";
    logger.error(`[physics] ${message}`);
    try {
      worker.terminate();
    } catch (error) {
      logger.warn("[physics] Failed to terminate stalled worker", error);
    }
    if (getPhysicsWorker() === worker) setPhysicsWorker(null);
    clearPhysicsWorkerWatchdog(worker);
    set({
      status: "error",
      lastError: message,
      startupStartedAt: null,
      isRunning: false,
      isPaused: false,
      lastStepHz: 0,
    });
  }, PHYSICS_WORKER_STALL_TIMEOUT_MS);
};

const notePhysicsWorkerProgress = (
  worker: Worker,
  get: () => PhysicsState,
  set: (partial: Partial<PhysicsState>) => void,
  payload?: WorkerLivenessPayload
) => {
  const state = get();
  if (state.status !== "running" || state.isPaused) {
    clearPhysicsWorkerWatchdog(worker);
    return;
  }

  if (payload && "simStepCount" in payload) {
    const nextStepCount = Number(payload.simStepCount);
    if (!Number.isFinite(nextStepCount)) return;
    if (livenessStepCount != null && nextStepCount <= livenessStepCount) return;
    livenessStepCount = nextStepCount;
  }

  armPhysicsWorkerWatchdog(worker, get, set);
};

// Simulation status types
export type PhysicsStatus = "idle" | "starting" | "running" | "paused" | "stopped" | "error";

// Pose tuple for a body: [px, py, pz, qw, qx, qy, qz]
export type BodyPose = [number, number, number, number, number, number, number];
export type BodyPoseBuffer = BodyPose[] | Float32Array | Float64Array;

interface LatestPosesPayload {
  bodyNames: string[];
  poses: BodyPoseBuffer;
  version: number;
}

type DiagnosticLevel = "info" | "warn" | "error";

export interface PhysicsDiagnostic {
  id: number;
  timestamp: number;
  level: DiagnosticLevel;
  source: string;
  stage?: string;
  message: string;
  data?: unknown;
}

//

export interface PhysicsState {
  status: PhysicsStatus;
  lastError: string | null;
  startupStartedAt: number | null;
  isRunning: boolean;
  isPaused: boolean;
  timestep: number; // seconds per step
  realtimeScale: number; // 1.0 = real time, 2.0 = 2x faster, etc.
  solverIterations: number;
  solverTolerance: number;
  lastStepHz: number;
  engine: "unknown" | "mujoco" | "fallback";
  // Mapping diagnostics
  mappedBodies: number;
  totalBodies: number;
  // Latest poses from physics (for subscribers)
  latestPoses: LatestPosesPayload | null;
  // Load strategy for worker
  loadMode: "full" | "noInertials" | "minimal" | "noMeshes";
  // Initial joint positions when physics started (for restoration on stop)
  initialJointPositions: Record<string, number> | null;
  // Flag to prevent baseline sync during coordinate system changes
  coordinateSystemChanging: boolean;
  // Disable conversion from MuJoCo world to viewer group rotation
  poseNoGroupConversion: boolean;
  // Debug snapshot
  debugInfo?: unknown;
  lastWorkerFailureDetails?: unknown;
  diagnostics: PhysicsDiagnostic[];
  // Snapshot of scene store state at physics start for restoration on stop
  startSnapshot?: Scene | SnapshotWithScene | null;

  // Sensor metadata from physics engine: name → {adr, dim, index}
  sensorMeta: Array<{ index: number; name: string; adr: number; dim: number }> | null;

  // Actuator controls/targets (single source of truth for the Physics panel)
  // Keys are physics actuator "control keys" (i.e., actuator names in the *generated XML*).
  actuatorPositionTargets: Record<string, number>;
  actuatorMotorControls: Record<string, number>;

  actions: {
    startFromXml: (
      xmlString: string,
      assets: Record<string, ArrayBuffer>,
      bodyNamesHint?: string[],
      options?: PhysicsStartOptions
    ) => Promise<void>;
    stop: (options?: { restoreScene?: boolean }) => Promise<void>;
    resetToStart: () => Promise<void>;
    pause: () => void;
    resume: () => void;
    reset: () => void;
    setTimestep: (dt: number) => void;
    setRealtimeScale: (scale: number) => void;
    setSolverIterations: (iterations: number) => void;
    setSolverTolerance: (tolerance: number) => void;
    setControls: (controls: Record<string, number>) => void;
    notifyStartupHoldModelReady: (payload?: { modelId?: string; nodeId?: string }) => void;
    setMappingStats: (mapped: number, total: number) => void;
    setLatestPoses: (bodyNames: string[], poses: BodyPoseBuffer) => void;
    setLoadMode: (mode: "full" | "noInertials" | "minimal" | "noMeshes") => void;
    requestDebugDump: () => void;
    exportWorkerXml: () => void;
    exportMainXml: () => void;
    addDiagnostic: (
      entry: Omit<PhysicsDiagnostic, "id" | "timestamp"> & { timestamp?: number }
    ) => void;
    setActuatorPositionTarget: (key: string, value: number) => void;
    mergeActuatorPositionTargets: (targets: Record<string, number>) => void;
    setActuatorMotorControl: (key: string, value: number) => void;
    mergeActuatorMotorControls: (controls: Record<string, number>) => void;
    resetActuatorControls: () => void;
    setPoseNoGroupConversion: (disabled: boolean) => void;
  };
}

export const usePhysicsStore = create<PhysicsState>((set, get) => ({
  status: "idle",
  lastError: null,
  startupStartedAt: null,
  isRunning: false,
  isPaused: false,
  timestep: 0.002, // 500 Hz default
  realtimeScale: 1.0,
  solverIterations: 100,
  solverTolerance: 1e-8,
  lastStepHz: 0,
  engine: "unknown",
  mappedBodies: 0,
  totalBodies: 0,
  latestPoses: null,
  loadMode: "full",
  initialJointPositions: null,
  coordinateSystemChanging: false, // Flag to prevent baseline sync during coord changes
  poseNoGroupConversion: false,
  debugInfo: undefined,
  lastWorkerFailureDetails: undefined,
  diagnostics: [],
  startSnapshot: null,
  actuatorPositionTargets: {},
  actuatorMotorControls: {},
  sensorMeta: null,

  actions: {
    startFromXml: async (xmlString, assets, bodyNamesHint, options) => {
      const state = get();
      const preserveActuatorControls = options?.preserveActuatorControls === true;
      const startupPosePolicy: StartupPosePolicy = options?.startupPosePolicy || "verbatimXmlPose";
      const captureStartSnapshot = options?.captureStartSnapshot !== false;
      const actuatorNamingMode = inferActuatorControlNamingModeFromXml(xmlString);
      const getStartupSceneState = () =>
        (options?.overrideSceneStateForKeyframe as ReturnType<typeof getSceneState> | undefined) ??
        getSceneState();
      const hasXmlInitQpos = /<\s*key\b[^>]*\bname\s*=\s*"init"[^>]*\bqpos\s*=/i.test(xmlString);
      const isPositionLikeActuator = (actuator: PositionLikeActuator) => {
        const kind = String(actuator?.kind || "").toLowerCase();
        if (kind === "position" || kind === "intposition") return true;
        if (kind !== "general") return false;
        // MuJoCo "general" is commonly used for PD position servos in robot MJCFs.
        // Treat joint- and tendon-coupled general actuators as position-like for startup seeding.
        return !!actuator?.joint || !!actuator?.tendon;
      };
      setActiveActuatorControlNamingMode(actuatorNamingMode);
      const storedPositionTargets = state.actuatorPositionTargets || {};
      const storedMotorControls = state.actuatorMotorControls || {};

      // Prevent multiple simultaneous initialization attempts
      if (state.status === "starting") {
        devLog("[physics] Already starting, ignoring duplicate start request");
        return;
      }

      const threadedWasmSupport = getThreadedWasmSupport();
      if (!threadedWasmSupport.ok) {
        logger.warn("[physics] MuJoCo threaded WASM unavailable", {
          reason: threadedWasmSupport.reason,
          crossOriginIsolated:
            typeof globalThis.crossOriginIsolated === "boolean"
              ? globalThis.crossOriginIsolated
              : null,
          hasSharedArrayBuffer: typeof globalThis.SharedArrayBuffer !== "undefined",
          hasWebAssembly: typeof globalThis.WebAssembly !== "undefined",
        });
        addBreadcrumb(
          "physics_threaded_wasm_unavailable",
          {
            area: "physics",
            action: "startFromXml",
            operation: "physics-run",
            phase: "preflight",
            reason: threadedWasmSupport.reason,
          },
          "warning"
        );
        set({
          status: "error",
          lastError: threadedWasmSupport.message,
          startupStartedAt: null,
          isRunning: false,
          isPaused: false,
        });
        showPhysicsCapabilityUnavailableToast(threadedWasmSupport.message);
        return;
      }

      if (isLocalPhysicsFailureTestEnabled()) {
        const message =
          "Local test forced physics startup failure. Remove ?forcePhysicsFailure from the URL to run physics normally.";
        logger.warn("[physics] Local forced physics failure enabled", {
          query: globalThis.location?.search || "",
        });
        addBreadcrumb(
          "physics_local_forced_failure",
          {
            area: "physics",
            action: "startFromXml",
            operation: "physics-run",
            phase: "preflight",
          },
          "warning"
        );
        set({
          status: "error",
          lastError: message,
          lastWorkerFailureDetails: {
            source: "local-force-physics-failure",
            url: globalThis.location?.href || "",
          },
          startupStartedAt: null,
          isRunning: false,
          isPaused: false,
        });
        showPhysicsErrorToast(message);
        return;
      }

      clearPendingHelloTimeout();
      const existingWorker = getPhysicsWorker();
      if (existingWorker) {
        clearPhysicsWorkerWatchdog(existingWorker);
        existingWorker.terminate();
        setPhysicsWorker(null);
      }

      // Save a full snapshot of scene state for restoration on stop (independent of history undo)
      const beforeState = getSceneState();
      addBreadcrumb("physics_start_requested", {
        area: "physics",
        action: "startFromXml",
        operation: "physics-run",
        phase: "start-requested",
        loadMode: state.loadMode,
        startupPosePolicy,
        preserveActuatorControls,
        assetCount: Object.keys(assets || {}).length,
        ...getSceneObservabilitySummary(beforeState),
      });
      let startSnapshot: Scene | null = null;
      if (captureStartSnapshot) {
        try {
          // Store only the scene payload (not the full HistorySnapshot wrapper).
          startSnapshot = makeHistorySnapshot(beforeState)?.scene ?? beforeState;
        } catch (error) {
          logger.warn("[Physics] Failed to capture pre-start scene snapshot", error);
          set({
            status: "error",
            lastError: "Failed to capture physics start snapshot",
            startupStartedAt: null,
            isRunning: false,
          });
          throw error;
        }
      }
      set({ startSnapshot });
      // Also keep legacy joint positions for debugging only
      const initialJointPositions = getJointValuesFromMap(beforeState.joints);
      devLog("[Physics] Saving initial joint positions:", initialJointPositions);

      set({
        status: "starting",
        lastError: null,
        lastWorkerFailureDetails: undefined,
        startupStartedAt: Date.now(),
        isPaused: false,
        isRunning: false,
        initialJointPositions,
      });

      try {
        devLog("[physics] Creating MuJoCo worker");
        addBreadcrumb("physics_worker_create_started", {
          area: "physics",
          action: "startFromXml",
          operation: "physics-run",
          phase: "worker-create",
          loadMode: get().loadMode,
        });
        const worker = new Worker(new URL("../physics/mujocoWorker.worker.js", import.meta.url), {
          type: "module",
          credentials: "same-origin",
        });
        devLog("[physics] Worker created successfully");

        worker.onmessage = createPhysicsWorkerMessageHandler({
          worker,
          get,
          set,
          onReady: () => {
            devLog("[physics] Worker ready, sending initialization data...");
            devLog("[physics] Ready message received from worker");
            addBreadcrumb("physics_worker_ready", {
              area: "physics",
              action: "startFromXml",
              operation: "physics-run",
              phase: "worker-ready",
              loadMode: get().loadMode,
            });
            // Clear the hello timeout since worker responded.
            clearPendingHelloTimeout(worker);
            devLog("[physics] Sending init payload to worker");

            // Refresh global actuator mapping before init (needed even when initialControls is null).
            try {
              const sceneState = getStartupSceneState();
              const actuators = sceneState.actuators;
              const joints: JointRecord = sceneState.joints || {};
              if (typeof actuators === "object" && actuators !== null) {
                const jointList = joints ? Object.values(joints) : [];
                updateGlobalActuatorMapping(Object.values(actuators), jointList, {
                  bodies: sceneState.bodies || {},
                  worldBodies: sceneState.worldBodies || [],
                  equalities: sceneState.equality || {},
                  joints: joints || {},
                });
              }
            } catch (e) {
              logger.warn("[Physics] Failed to build actuator mapping before worker init", e);
            }

            try {
              if (preserveActuatorControls) {
                set({
                  actuatorPositionTargets: { ...storedPositionTargets },
                  actuatorMotorControls: { ...storedMotorControls },
                });
              }
            } catch (e) {
              logger.warn("[Physics] Failed to apply preserved actuator controls before init", e);
            }

            const wasmRuntimeInfo = getMujocoWasmRuntimeInfo();
            logger.info(
              `[physics] Starting MuJoCo WASM runtime: ${wasmRuntimeInfo.label} (${wasmRuntimeInfo.detail})`
            );
            worker.postMessage({
              type: "init",
              payload: {
                xmlString,
                assets,
                timestep: get().timestep,
                realtimeScale: get().realtimeScale,
                solverIterations: get().solverIterations,
                solverTolerance: get().solverTolerance,
                loadMode: get().loadMode,
                bodyNamesHint: Array.isArray(bodyNamesHint) ? bodyNamesHint : undefined,
                // Keep startup pose source explicit via startupPosePolicy + XML generation mode.
                initialJointMap: (() => {
                  if (startupPosePolicy !== "currentScenePose") return null;
                  try {
                    return buildCurrentSceneInitialJointMap(xmlString, getStartupSceneState());
                  } catch (error) {
                    logger.warn(
                      "[Physics] Failed to build current-scene initial joint map:",
                      error
                    );
                  }
                  return null;
                })(),
                startupPosePolicy,
                preserveActuatorControls,
                initialFreeJointPose: (() => {
                  try {
                    const sceneState = getStartupSceneState();
                    return getInitialFreeJointPoseFromScene(sceneState);
                  } catch (error) {
                    logger.warn("[Physics] Failed to get initial free joint pose:", error);
                  }
                  return null;
                })(),
                initialControls: (() => {
                  try {
                    if (
                      !preserveActuatorControls &&
                      hasXmlInitQpos &&
                      startupPosePolicy === "verbatimXmlPose"
                    ) {
                      return null;
                    }
                    const sceneState = getStartupSceneState();
                    const jointValues = getJointValuesFromMap(sceneState.joints);
                    const actuators = sceneState.actuators;
                    const joints: JointRecord = sceneState.joints || {};
                    const jointList = joints ? Object.values(joints) : [];
                    const bodies = sceneState.bodies || {};
                    const worldBodies = sceneState.worldBodies || [];
                    const bodyRootMap = buildBodyRootMap(bodies, worldBodies);

                    const resolveControlName = (actuator: PositionLikeActuator) => {
                      if (actuator?.uuid) {
                        return generateStableActuatorNameFromUuid(actuator.uuid);
                      }
                      const jointUuid = typeof actuator?.joint === "string" ? actuator.joint : null;
                      const hostBodyUuid = jointUuid
                        ? findBodyForJointUuid(bodies, jointUuid)
                        : null;
                      const rootUuid = hostBodyUuid
                        ? bodyRootMap[hostBodyUuid] || hostBodyUuid
                        : null;
                      const jointName = jointUuid ? joints?.[jointUuid]?.name : null;
                      const baseName =
                        actuator?.name || jointName || actuator?.tendon || "actuator";
                      return generateActuatorControlName({
                        baseName,
                        jointUuid,
                        rootBodyUuid: rootUuid || null,
                        tendonName: actuator?.tendon || null,
                      });
                    };

                    // Update global actuator mapping
                    if (typeof actuators === "object") {
                      const equalities = sceneState.equality || {};
                      const mappingResult = updateGlobalActuatorMapping(
                        Object.values(actuators),
                        jointList,
                        { bodies, worldBodies, equalities, joints }
                      );

                      // Log mapping results
                      if (!mappingResult.success) {
                        logger.warn("[Physics] Actuator mapping had errors:", mappingResult.errors);
                        get().actions.addDiagnostic({
                          level: "error",
                          message: `Actuator mapping failed: ${mappingResult.errors.join(", ")}`,
                          source: "physics-init",
                          stage: "actuator-mapping",
                          data: {
                            errors: mappingResult.errors,
                            warnings: mappingResult.warnings,
                          },
                        });
                      } else if (mappingResult.warnings.length > 0) {
                        if (import.meta.env?.DEV)
                          logger.info(
                            "[Physics] Actuator mapping warnings:",
                            mappingResult.warnings
                          );
                        get().actions.addDiagnostic({
                          level: "warn",
                          message: `Actuator mapping warnings: ${mappingResult.warnings.length} warnings`,
                          source: "physics-init",
                          stage: "actuator-mapping",
                          data: {
                            warnings: mappingResult.warnings,
                            mappedCount: mappingResult.mappedCount,
                          },
                        });
                      }

                      devLog(
                        `[Physics] Actuator mapping completed: ${mappingResult.mappedCount} actuators mapped${mappingResult.fromCache ? " (from cache)" : ""}`
                      );
                    }

                    // Initialize controls to hold current positions or preserve slider state
                    const actuatorValues: Record<string, number> = {};
                    const preservedPositionTargets: Record<string, number> = {};
                    const preservedMotorControls: Record<string, number> = {};

                    if (preserveActuatorControls) {
                      Object.entries(storedPositionTargets).forEach(([key, value]) => {
                        const v = Number(value);
                        if (Number.isFinite(v)) preservedPositionTargets[key] = v;
                      });
                      Object.entries(storedMotorControls).forEach(([key, value]) => {
                        const v = Number(value);
                        if (Number.isFinite(v)) preservedMotorControls[key] = v;
                      });
                    }

                    if (!preserveActuatorControls && typeof actuators === "object") {
                      const equalities = Object.values(sceneState.equality || {}) as Equality[];
                      const tendonActuators = Object.values(actuators).filter(
                        (a): a is PositionLikeActuator => !!a?.tendon
                      );
                      const tendonJointEqualities = equalities.filter(
                        (eq) =>
                          !!eq &&
                          eq.type === "joint" &&
                          (eq.objtype === "joint" || !eq.objtype) &&
                          typeof eq.obj1 === "string" &&
                          typeof eq.obj2 === "string" &&
                          joints?.[eq.obj1] &&
                          joints?.[eq.obj2]
                      );
                      Object.values(actuators).forEach((actuator) => {
                        if (!actuator) return;

                        // actuator.joint may be a UUID or a joint name (e.g. "joint1" from parsed XML).
                        const jointUuid = actuator.joint;
                        if (jointUuid) {
                          const joint =
                            (jointUuid ? joints[jointUuid] : undefined) ||
                            Object.values(joints || {}).find((j) => j?.name === jointUuid) ||
                            null;
                          const jointName = joint?.name;
                          if (jointName && jointValues[jointName] != null) {
                            // For position control, set target to current position
                            // For motor control, keep at zero (no torque)
                            if (isPositionLikeActuator(actuator)) {
                              const actuatorKey = resolveControlName(actuator);
                              if (actuatorKey) {
                                actuatorValues[actuatorKey] = mapAffinePositionTargetToControl(
                                  jointValues[jointName],
                                  actuator.gainprm,
                                  actuator.biasprm
                                );
                              }
                            }
                          }
                          return;
                        }

                        // Tendon actuators: initial ctrl = scene tendon length (sum coef × joint ref).
                        if (actuator.tendon && isPositionLikeActuator(actuator)) {
                          const actuatorKey = resolveControlName(actuator);
                          if (!actuatorKey) return;

                          const tendonIndex = tendonActuators.findIndex(
                            (a) =>
                              a === actuator ||
                              (!!a?.uuid && !!actuator?.uuid && a.uuid === actuator.uuid) ||
                              (a?.name === actuator?.name && a?.tendon === actuator?.tendon)
                          );
                          const matchedEq =
                            tendonIndex >= 0 && tendonIndex < tendonJointEqualities.length
                              ? tendonJointEqualities[tendonIndex]
                              : null;
                          const candidateJointUuids = matchedEq
                            ? [matchedEq.obj1, matchedEq.obj2]
                            : [];
                          const normalizedValues = candidateJointUuids
                            .map((ju) => {
                              if (!ju) return null;
                              const j = joints?.[String(ju)];
                              if (!j || (j.kind !== "slide" && j.kind !== "hinge")) return null;
                              const ref = Number(j.ref);
                              const lo = Number(j.range?.[0]);
                              const hi = Number(j.range?.[1]);
                              if (
                                !Number.isFinite(ref) ||
                                !Number.isFinite(lo) ||
                                !Number.isFinite(hi) ||
                                hi <= lo
                              ) {
                                return null;
                              }
                              return Math.max(0, Math.min(1, (ref - lo) / (hi - lo)));
                            })
                            .filter((v: number | null): v is number => v != null);

                          const ctrlLo = Number(actuator.ctrlrange?.[0]);
                          const ctrlHi = Number(actuator.ctrlrange?.[1]);
                          const hasCtrlRange =
                            Number.isFinite(ctrlLo) && Number.isFinite(ctrlHi) && ctrlHi > ctrlLo;
                          if (!hasCtrlRange) return;

                          const tendonsMap = sceneState.tendons as TendonRecord | undefined;
                          const tendonDef =
                            actuator.tendonUuid && tendonsMap
                              ? tendonsMap[actuator.tendonUuid] ||
                                Object.values(tendonsMap).find(
                                  (t) => t?.uuid === actuator.tendonUuid
                                )
                              : tendonsMap
                                ? Object.values(tendonsMap).find((t) => t?.name === actuator.tendon)
                                : null;
                          const fromDef = getSceneFixedTendonLengthForActuator(
                            actuator,
                            tendonsMap,
                            joints
                          );
                          let controlTarget: number;
                          if (fromDef != null && Number.isFinite(Number(fromDef))) {
                            controlTarget = mapAffinePositionTargetToControl(
                              Number(fromDef),
                              actuator.gainprm,
                              actuator.biasprm
                            );
                          } else {
                            // Spatial tendon lengths depend on compiled MuJoCo routing and wrap geometry.
                            // Do not guess from joint refs or ctrlrange; the worker seeds these from ten_length.
                            if (tendonDef?.kind === "spatial") return;
                            if (normalizedValues.length > 0) {
                              const norm =
                                normalizedValues.reduce((sum: number, v: number) => sum + v, 0) /
                                normalizedValues.length;
                              controlTarget = ctrlLo + norm * (ctrlHi - ctrlLo);
                            } else {
                              controlTarget = ctrlLo;
                            }
                          }
                          actuatorValues[actuatorKey] = Math.max(
                            ctrlLo,
                            Math.min(ctrlHi, controlTarget)
                          );
                        }
                      });
                    }

                    const actuatorList = actuators ? Object.values(actuators) : [];
                    if (preserveActuatorControls) {
                      const positionPayload =
                        Object.keys(preservedPositionTargets).length > 0
                          ? createPositionActuatorControlPayload(
                              preservedPositionTargets,
                              actuatorList
                            )
                          : {};
                      const motorPayload =
                        Object.keys(preservedMotorControls).length > 0
                          ? createActuatorControlPayload(preservedMotorControls, actuatorList)
                          : {};
                      const payload = { ...positionPayload, ...motorPayload };
                      return Object.keys(payload).length > 0 ? payload : null;
                    }

                    // Non-preserved startup controls above are already raw MuJoCo ctrl values.
                    return Object.keys(actuatorValues).length > 0
                      ? createActuatorControlPayload(actuatorValues, actuatorList)
                      : null;
                  } catch (e) {
                    logger.error("[Physics] Error creating initialControls:", e);
                    return null;
                  }
                })(),
              },
            });
          },
          onWorkerProgress: (payload) => {
            notePhysicsWorkerProgress(worker, get, set, payload);
          },
          clearWorkerWatchdog: clearPhysicsWorkerWatchdog,
          clearPendingHelloTimeout,
          devLog,
        });
        worker.onerror = (e) => {
          clearPendingHelloTimeout(worker);
          clearPhysicsWorkerWatchdog(worker);
          logger.error("[physics] Worker error:", e);
          const workerFailureDetails = {
            source: "worker.onerror",
            message: e.message || "",
            filename: e.filename || "",
            lineno: e.lineno || 0,
            colno: e.colno || 0,
            workerScriptUrl: getLatestMujocoWorkerResourceUrl(),
            error: e.error
              ? {
                  name: e.error.name || "Error",
                  message: e.error.message || String(e.error),
                  stack: e.error.stack,
                }
              : null,
          };
          addBreadcrumb(
            "physics_worker_error",
            {
              area: "physics",
              action: "worker.onerror",
              operation: "physics-run",
              phase: "failed",
              loadMode: get().loadMode,
              engine: get().engine,
              mappedBodies: get().mappedBodies,
              totalBodies: get().totalBodies,
              ...getSceneObservabilitySummary(getSceneState()),
            },
            "error"
          );
          reportRuntimeError(
            e.error || e.message || e,
            {
              source: "physics-worker-error",
              filename: e.filename,
              lineno: e.lineno,
              colno: e.colno,
            },
            {
              toastKey: "physics-worker-error",
              toastMessage: physicsUserErrorToast(e.error || e.message || e),
              toastDetail: physicsErrorToastDetail,
            }
          );
          try {
            worker.terminate();
          } catch (error) {
            logger.warn("[physics] Failed to terminate errored worker", error);
          }
          setPhysicsWorker(null);
          set({
            status: "error",
            lastError: e.message || String(e),
            lastWorkerFailureDetails: workerFailureDetails,
            startupStartedAt: null,
            isRunning: false,
          });
        };

        worker.onmessageerror = (e) => {
          logger.error("[physics] Worker message error:", e);
          const workerFailureDetails = {
            source: "worker.onmessageerror",
            origin: e.origin || "",
            lastEventId: e.lastEventId || "",
            dataType: typeof e.data,
          };
          addBreadcrumb(
            "physics_worker_message_error",
            {
              area: "physics",
              action: "worker.onmessageerror",
              operation: "physics-run",
              phase: "failed",
              loadMode: get().loadMode,
              engine: get().engine,
            },
            "error"
          );
          reportRuntimeError(
            new Error("Physics worker message could not be deserialized"),
            {
              source: "physics-worker-messageerror",
            },
            {
              toastKey: "physics-worker-messageerror",
              toastMessage: physicsUserErrorToast(
                "Physics worker message could not be deserialized"
              ),
              toastDetail: physicsErrorToastDetail,
            }
          );
          set({ lastWorkerFailureDetails: workerFailureDetails });
        };

        // Add timeout to detect if worker doesn't respond.
        // Use a generous window: on slower machines or cold starts, module worker
        // boot can exceed a few seconds even when healthy.
        clearPendingHelloTimeout();
        pendingHelloWorker = worker;
        pendingHelloTimeout = setTimeout(() => {
          // Ignore stale timers from workers that have since been replaced/terminated.
          if (pendingHelloWorker !== worker) return;
          clearPendingHelloTimeout(worker);
          clearPhysicsWorkerWatchdog(worker);
          logger.error("[physics] Worker did not respond to hello message within 20 seconds");
          addBreadcrumb(
            "physics_worker_hello_timeout",
            {
              area: "physics",
              action: "startFromXml",
              operation: "physics-run",
              phase: "hello-timeout",
              loadMode: get().loadMode,
              ...getSceneObservabilitySummary(getSceneState()),
            },
            "error"
          );
          logger.error(
            "[physics] This suggests the worker script has a syntax error or loading issue"
          );
          logger.error("[physics] Check browser console for worker script errors");
          try {
            worker.terminate();
          } catch (error) {
            logger.warn("[physics] Failed to terminate timed-out worker", error);
          }
          setPhysicsWorker(null);
          set({
            status: "error",
            lastError: "Worker initialization timeout - no response to hello message",
            startupStartedAt: null,
            isRunning: false,
          });
        }, 20000);
      } catch (e) {
        clearPendingHelloTimeout();
        clearPhysicsWorkerWatchdog();
        const message = e instanceof Error ? e.message : String(e);
        addBreadcrumb(
          "physics_worker_start_failed",
          {
            area: "physics",
            action: "startFromXml",
            operation: "physics-run",
            phase: "failed",
            loadMode: get().loadMode,
            ...getSceneObservabilitySummary(getSceneState()),
          },
          "error"
        );
        reportRuntimeError(
          e,
          {
            source: "physics-worker-start",
          },
          {
            toastKey: "physics-worker-start",
            toastMessage: physicsUserErrorToast(e),
            toastDetail: physicsErrorToastDetail,
          }
        );
        set({
          status: "error",
          lastError: message,
          startupStartedAt: null,
          isRunning: false,
        });
      }
    },

    stop: async (options) => {
      const state = get();
      const w = getPhysicsWorker();
      const restoreScene = options?.restoreScene !== false;

      devLog(`[Physics] Stopping physics${restoreScene ? " and resetting to initial state" : ""}`);
      addBreadcrumb("physics_stop_requested", {
        area: "physics",
        action: "stop",
        operation: "physics-run",
        phase: "stop-requested",
        status: state.status,
        engine: state.engine,
        mappedBodies: state.mappedBodies,
        totalBodies: state.totalBodies,
      });

      // Prevent stale startup timers from firing after explicit stop/restart flows.
      clearPendingHelloTimeout();
      clearPhysicsWorkerWatchdog(w);
      // Terminate the worker
      if (w) w.terminate();
      setPhysicsWorker(null);

      set({
        isRunning: false,
        isPaused: false,
        sensorMeta: null,
      });

      if (restoreScene) {
        // Restore full scene state from the saved snapshot
        try {
          const { restoreState } = getSceneActions();
          const snapshot = get().startSnapshot;
          if (snapshot && typeof restoreState === "function") {
            const sceneSnapshot =
              snapshot && typeof snapshot === "object" && "scene" in snapshot
                ? (snapshot as SnapshotWithScene).scene
                : snapshot;
            if (sceneSnapshot) restoreState(sceneSnapshot as Scene);
            devLog("[Physics] Restored pre-start scene state from snapshot");
          } else {
            logger.warn("[Physics] No start snapshot available; skipping restore");
          }
          // Fire cleanup event first (before rebuild) so listeners act on the old scene state.
          try {
            window.dispatchEvent(new CustomEvent("physics-stopped-reset"));
          } catch (error) {
            logger.warn("[Physics] Failed to dispatch physics-stopped-reset on stop", error);
          }
          // Force a full scene rebuild signal so Scene and panels reconstruct from store.
          try {
            window.dispatchEvent(new CustomEvent("history-restored"));
          } catch (error) {
            logger.warn("[Physics] Failed to dispatch history-restored on stop", error);
          }
          try {
            window.dispatchEvent(new CustomEvent("structure-changed"));
          } catch (error) {
            logger.warn("[Physics] Failed to dispatch structure-changed on stop", error);
          }
        } catch (e) {
          logger.warn("[Physics] Failed to restore state from snapshot:", e);
        }
      }

      set({
        status: "stopped",
        startupStartedAt: null,
        initialJointPositions: null,
        startSnapshot: null,
        actuatorMotorControls: restoreScene ? {} : state.actuatorMotorControls,
        actuatorPositionTargets: restoreScene ? {} : state.actuatorPositionTargets,
      });

      try {
        window.dispatchEvent(
          new CustomEvent("physics-stopped", {
            detail: { restoreScene },
          })
        );
      } catch (error) {
        logger.warn("[Physics] Failed to dispatch physics-stopped event", error);
      }

      if (restoreScene) {
        // Notify UI to reset actuator slider values
        try {
          window.dispatchEvent(new CustomEvent("physics-reset-controls"));
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-reset-controls", error);
        }
      }
    },

    resetToStart: async () => {
      // Just call stop - they do the same thing now
      await get().actions.stop();
    },

    pause: () => {
      const w = getPhysicsWorker();
      if (w) w.postMessage({ type: "pause" });
      clearPhysicsWorkerWatchdog(w);
      set({ isPaused: true, status: "paused" });
    },

    resume: () => {
      const w = getPhysicsWorker();
      if (w) w.postMessage({ type: "resume" });
      set({ isPaused: false, status: "running" });
      if (w) notePhysicsWorkerProgress(w, get, set);
    },

    reset: () => {
      const w = getPhysicsWorker();
      if (w) w.postMessage({ type: "reset" });
    },

    setTimestep: (dt: number) => {
      const raw = Number(dt);
      const clamped =
        Number.isFinite(raw) && raw > 0 ? Math.min(0.1, Math.max(1e-6, raw)) : get().timestep;
      if (!Number.isFinite(raw) || raw <= 0 || clamped !== raw) {
        logger.warn("[physics] Invalid timestep ignored:", dt, "→ using", clamped);
      }
      set({ timestep: clamped });
    },
    setRealtimeScale: (scale: number) => {
      const raw = Number(scale);
      const clamped =
        Number.isFinite(raw) && raw > 0 ? Math.min(100, Math.max(0.001, raw)) : get().realtimeScale;
      if (!Number.isFinite(raw) || raw <= 0 || clamped !== raw) {
        logger.warn("[physics] Invalid realtime scale ignored:", scale, "→ using", clamped);
      }
      const w = getPhysicsWorker();
      if (w) {
        w.postMessage({
          type: "setRealtimeScale",
          payload: { realtimeScale: clamped },
        });
      }
      set({ realtimeScale: clamped });
    },
    setSolverIterations: (iterations: number) =>
      set({ solverIterations: Math.max(1, Math.floor(Number(iterations) || 1)) }),
    setSolverTolerance: (tolerance: number) =>
      set({
        solverTolerance: Math.max(1e-12, Number.isFinite(tolerance) ? Number(tolerance) : 1e-8),
      }),
    setControls: (controls: Record<string, number>) => {
      const w = getPhysicsWorker();
      if (w) w.postMessage({ type: "controls", payload: controls });
    },
    notifyStartupHoldModelReady: (payload?: { modelId?: string; nodeId?: string }) => {
      const w = getPhysicsWorker();
      if (!w) return;
      w.postMessage({
        type: "startupHoldModelReady",
        payload: {
          modelId: payload?.modelId || null,
          nodeId: payload?.nodeId || null,
        },
      });
    },
    setMappingStats: (mapped: number, total: number) =>
      set({ mappedBodies: mapped, totalBodies: total }),
    setLatestPoses: (bodyNames: string[], poses: BodyPoseBuffer) => {
      const poseStride = 7;
      set({
        latestPoses: {
          bodyNames,
          poses,
          version: (get().latestPoses?.version || 0) + 1,
        },
      });
      const poseCount = ArrayBuffer.isView(poses)
        ? Math.floor(poses.length / poseStride)
        : poses.length;
      devLog("[PhysicsStore] setLatestPoses", { bodyNames, poseCount });
    },
    setLoadMode: (mode: "full" | "noInertials" | "minimal" | "noMeshes") => set({ loadMode: mode }),
    requestDebugDump: () => {
      const w = getPhysicsWorker();
      if (!w) {
        logger.warn("[physics] requestDebugDump: no worker");
        return;
      }
      devLog("[physics] requestDebugDump: sending debugDump");
      w.postMessage({ type: "debugDump" });
    },
    exportWorkerXml,
    exportMainXml,
    addDiagnostic: (entry) => {
      const now = entry?.timestamp || Date.now();
      const id = now + Math.floor(Math.random() * 1000);
      set((state) => {
        const next = [
          ...state.diagnostics,
          {
            id,
            timestamp: now,
            level: entry?.level || "info",
            source: entry?.source || "app",
            stage: entry?.stage,
            message: entry?.message || "",
            data: entry?.data,
          },
        ];
        const maxEntries = 200;
        return { diagnostics: next.slice(Math.max(0, next.length - maxEntries)) };
      });
    },

    setActuatorPositionTarget: (key: string, value: number) => {
      if (!key) return;
      const v = Number(value);
      if (!Number.isFinite(v)) return;
      set((s) => {
        if (s.actuatorPositionTargets[key] === v) return s;
        return {
          actuatorPositionTargets: { ...s.actuatorPositionTargets, [key]: v },
        };
      });
    },
    mergeActuatorPositionTargets: (targets: Record<string, number>) => {
      if (!targets || typeof targets !== "object") return;
      set((s) => {
        let hasChanges = false;
        const nextTargets = { ...s.actuatorPositionTargets };
        for (const [key, value] of Object.entries(targets)) {
          const v = Number(value);
          if (!key || !Number.isFinite(v)) continue;
          if (s.actuatorPositionTargets[key] !== v) {
            hasChanges = true;
            nextTargets[key] = v;
          }
        }
        if (!hasChanges) return s;
        return {
          actuatorPositionTargets: nextTargets,
        };
      });
    },
    setActuatorMotorControl: (key: string, value: number) => {
      if (!key) return;
      const v = Number(value);
      if (!Number.isFinite(v)) return;
      set((s) => {
        if (s.actuatorMotorControls[key] === v) return s;
        return {
          actuatorMotorControls: { ...s.actuatorMotorControls, [key]: v },
        };
      });
    },
    mergeActuatorMotorControls: (controls: Record<string, number>) => {
      if (!controls || typeof controls !== "object") return;
      set((s) => {
        let hasChanges = false;
        const nextControls = { ...s.actuatorMotorControls };
        for (const [key, value] of Object.entries(controls)) {
          const v = Number(value);
          if (!key || !Number.isFinite(v)) continue;
          if (s.actuatorMotorControls[key] !== v) {
            hasChanges = true;
            nextControls[key] = v;
          }
        }
        if (!hasChanges) return s;
        return {
          actuatorMotorControls: nextControls,
        };
      });
    },
    resetActuatorControls: () => set({ actuatorMotorControls: {}, actuatorPositionTargets: {} }),
    setPoseNoGroupConversion: (disabled: boolean) =>
      set({ poseNoGroupConversion: disabled === true }),
  },
}));
